import { useState } from "react";
import { useTranslation } from "react-i18next";
import api from "../../../api";
import { PayBookingsRequest } from "../../../api/types";
import useShowToast from "../../../hooks/useShowToast";

/**
 * Custom hook to pay selected unpaid bookings from management.
 * @returns Object containing the payBookings function and loading state
 */
function useManagementPayBookings() {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const { successToast, errorToast } = useShowToast();

  const payBookings = async (bookingIds: number[]): Promise<boolean> => {
    if (bookingIds.length === 0) {
      return false;
    }

    setIsLoading(true);

    try {
      // Get receipt preview to get name, item_price, and item_count
      const receiptPreview = await api.getReceiptPreview(bookingIds);

      if (api.error || !receiptPreview) {
        errorToast({
          title: t("club_management.unpaid_hours.pay.error_title"),
          description: t("club_management.unpaid_hours.pay.error_preview"),
        });
        setIsLoading(false);
        return false;
      }

      // Pay the bookings
      const payRequest: PayBookingsRequest = {
        booking_ids: bookingIds,
        name: receiptPreview.name,
        item_price: receiptPreview.item_price,
        item_count: receiptPreview.item_count,
      };

      const response = await api.payBookings(payRequest);

      if (api.error || !response) {
        errorToast({
          title: t("club_management.unpaid_hours.pay.error_title"),
          description: t("club_management.unpaid_hours.pay.error_description"),
        });
        setIsLoading(false);
        return false;
      }

      successToast({
        title: t("club_management.unpaid_hours.pay.success_title"),
        description: t("club_management.unpaid_hours.pay.success_description"),
      });

      setIsLoading(false);
      return true;
    } catch (error) {
      errorToast({
        title: t("club_management.unpaid_hours.pay.error_title"),
        description: t("club_management.unpaid_hours.pay.error_description"),
      });
      setIsLoading(false);
      return false;
    }
  };

  return {
    payBookings,
    isLoading,
  };
}

export default useManagementPayBookings;
